import * as jsv2 from 'jsv2';
console.log("jsv2 loaded", jsv2);

const GRAPH_ID = "graph-1";

const DATA_TYPES = {
    "nmrML_1D": { name: "1D-NMR",  expectedDataTag: "spectrumDataArray" },
    "nmrML_2D": { name: "2D-NMR",  expectedDataTag: "spectrumDataArray" },
    "lc_ms_1D": { name: "1D-LCMS", expectedDataTag: "binary" },
    "ei_ms_1D": { name: "1D-EIMS", expectedDataTag: "binary" },
    "ir_1D":    { name: "1D-IR",   expectedDataTag: "ms-ir-spectrum" },
    "uv_1D":    { name: "1D-UV",   expectedDataTag: null },
};

let viewer = null;
let primaryParser = null;
let mirrorParser = null;
let currentFileType = "nmrML_1D";
let lastIrEvent = null;   // kept so the IR plot can be redrawn cleanly when the mirror is removed

function getResponsiveWidth() {
    const container = document.getElementById(GRAPH_ID);
    if (container) return Math.max((container.offsetWidth || container.clientWidth) - 20, 800);
    return 1170;
}

// -----------------------------------------------------------------------
// Boot: runs on every page load/navigation
// -----------------------------------------------------------------------
let _booted = false;

function bootIfHomePage() {
    const uploadForm = document.getElementById("upload-form");
    if (!uploadForm) return; // not on home page
    if (_booted) return;    // don't run twice (DOMContentLoaded + turbo:load both fire on first visit)
    _booted = true;

    // Sync currentFileType from dropdown
    const dropdown = document.getElementById("input-file-type");
    if (dropdown) currentFileType = dropdown.value;

    // Set up primary DataParser on #file-selector
    const fileInput = document.getElementById("file-selector");
    if (fileInput) setupDataParser(fileInput, currentFileType);

    // Always set up mirror parser too — it just waits silently until a file is picked.
    // Don't wait for the toggle; create it at boot so timing is never an issue.
    setupMirrorParser(currentFileType);

    console.log("bootIfHomePage complete");
}

document.addEventListener("DOMContentLoaded", bootIfHomePage);
document.addEventListener("turbo:load", bootIfHomePage);
document.addEventListener("turbo:before-cache", function() {
    _booted = false;      // allow re-boot after Turbo navigation
    primaryParser = null; // force fresh parser on next boot (Turbo replaces DOM elements)
    mirrorParser = null;
    viewer = null;        // clear stale viewer reference
});

// teardownMirrorExternal is called from the slim toggle listener when toggled off
window.teardownMirrorExternal = function() {
    // IR uses a reversed Y axis, and jsv2's remove_mirror_spectrum doesn't undo the
    // inversion it applies to the primary spectrum, so redraw the IR plot from scratch.
    if (currentFileType === "ir_1D" && lastIrEvent) {
        plotIr1D(lastIrEvent);
        return;
    }
    if (viewer && typeof viewer.remove_mirror_spectrum === "function") {
        viewer.remove_mirror_spectrum();
    }
};
// setupMirrorParserExternal is a no-op — mirror parser is always created at boot
window.setupMirrorParserExternal = function() {};

// -----------------------------------------------------------------------
// Event delegation for file type dropdown — select elements propagate fine
// -----------------------------------------------------------------------
document.addEventListener("change", function(event) {
    if (event.target.id !== "input-file-type") return;
    currentFileType = event.target.value;
    const fileInput = document.getElementById("file-selector");
    if (fileInput) {
        setupDataParser(fileInput, currentFileType);
    }
    // Always re-create mirror parser for new file type
    setupMirrorParser(currentFileType);
    console.log("File type changed to:", currentFileType);
});

// Mirror toggle show/hide and parser setup is called from the slim inline
// javascript block via window.setupMirrorParserExternal / window.teardownMirrorExternal


// -----------------------------------------------------------------------
// PRIMARY parser
// -----------------------------------------------------------------------
function setupDataParser(fileInputElement, fileType) {
    if (primaryParser) {
        // Reuse existing parser — avoids stacking duplicate change listeners
        // on #file-selector each time the file type dropdown changes.
        primaryParser.set_data_type(DATA_TYPES[fileType].name);
        primaryParser.set_expected_data_tag(DATA_TYPES[fileType].expectedDataTag || 'spectrumDataArray');
        console.log("Primary parser type updated to:", fileType);
        return;
    }

    // First time: create parser and attach listener.
    // Callback reads currentFileType at call time (not closed-over fileType)
    // so it routes correctly after type changes.
    primaryParser = new jsv2.DataParser("#" + fileInputElement.id, {
        dataType: DATA_TYPES[fileType].name,
        expectedDataTag: DATA_TYPES[fileType].expectedDataTag || 'spectrumDataArray',
    });

    primaryParser.addEventListener("fileParsed", (event) => {
        console.log("File parsed event emitted", event);
        if      (currentFileType === "nmrML_1D") plotNmrml1D(event);
        else if (currentFileType === "ei_ms_1D") plotEims1D(event);
        else if (currentFileType === "nmrML_2D") plotNmrml2D(event);
        else if (currentFileType === "lc_ms_1D") plotLcms1D(event);
        else if (currentFileType === "ir_1D")    plotIr1D(event);
        else if (currentFileType === "uv_1D")    plotUv1D(event);
        else console.error("Unknown file type:", currentFileType);
    });

    console.log("DataParser initialized with file type:", primaryParser.data_type,
                "and expected data tag:", primaryParser.expected_data_tag);
}

// -----------------------------------------------------------------------
// MIRROR parser
// -----------------------------------------------------------------------
function setupMirrorParser(fileType) {
    const mirrorInput = document.getElementById("mirror-file-selector");
    if (!mirrorInput) {
        console.error("mirror-file-selector not found");
        return;
    }

    // Reuse existing parser — avoids stacking duplicate change listeners on
    // #mirror-file-selector every time the file type dropdown changes.
    if (mirrorParser) {
        mirrorParser.set_data_type(DATA_TYPES[fileType].name);
        mirrorParser.set_expected_data_tag(DATA_TYPES[fileType].expectedDataTag || 'spectrumDataArray');
        console.log("Mirror parser type updated to:", fileType);
        return;
    }

    mirrorParser = new jsv2.DataParser("#mirror-file-selector", {
        dataType: DATA_TYPES[fileType].name,
        expectedDataTag: DATA_TYPES[fileType].expectedDataTag || 'spectrumDataArray',
    });

    mirrorParser.addEventListener("fileParsed", (event) => {
        console.log("Mirror file parsed", event);
        if (!viewer) {
            alert("Load a primary spectrum first.");
            return;
        }
        viewer.add_mirror_spectrum({
            x: event.detail.x,
            y: event.detail.y,
            name: (event.detail.assignmentData && event.detail.assignmentData.name) || "Mirror Spectrum",
            color: "blue",
        });
    });

    console.log("Mirror parser initialized for file type:", fileType);
}

// -----------------------------------------------------------------------
// IR 1D
// -----------------------------------------------------------------------
function plotIr1D(event) {
    lastIrEvent = event;
    viewer = new jsv2.SpectraViewer1D("#" + GRAPH_ID, {
        name: "ir-viewer",
        width: getResponsiveWidth(),
        height: 400,
        margin: { top: 50, bottom: 50, right: 50, left: 90 },
        chartTitle: { text: "IR Spectrum", fontSize: 200, color: "black", underline: true },
        chartType: "1D-IR",
        simplifyTolerance: 0.005,
        dataset: [{
            dataArray: { x: event.detail.x, y: event.detail.y },
            isMirrorPlot: false,
            normalize: false,
            name: (event.detail.assignmentData && event.detail.assignmentData.name) || "IR Spectrum",
            style: { strokeColor: "red", strokeWidth: 1 },
        }],
        axis: {
            minBoundaries: { x: [550, 1900], y: [-0.05, 1.1] },
            x: { enabled: true, reverse: true, label: { text: "IR Frequency (cm\u207B\u00B9)", fontSize: 15 }, ticks: 20 },
            // reverse: peaks grow downward from the top, as IR spectra are conventionally drawn (jsv2 flips the zoombox to match)
            // tickFontOffset: shrink the Y tick numbers a few px so they don't overlap
            // gutterSpace: widened from the default 65 so the title clears the longer mirror-mode tick labels
            y: { enabled: true, reverse: true, tickFontOffset: -2, gutterSpace: 72, label: { text: "Normalized Intensity", fontSize: 15 }, ticks: 11 },
        },
        grid: {
            horizontal: { enabled: true, strokeColor: "steelblue", strokeWidth: 0.5, opacity: 0.1, ticks: 15 },
            vertical:   { enabled: true, strokeColor: "black",     strokeWidth: 1,   opacity: 1,   ticks: 10 },
        },
        zoomBox: { enabled: true, position: "top-right", width: 200, height: 100 },
        controls: {
            enabled: true,
            options: {
                zoom: { vertical: true, horizontal: true, max: 100 },
                pan:  { vertical: true, horizontal: true },
                reset: true,
                download: { formats: ["png", "svg", "pdf"], filename: "ir_spectrum" },
            },
        },
    });
    viewer.draw();
    window._jsvViewer = viewer;
    console.log("Viewer drawn for IR 1D spectrum.");
}

// -----------------------------------------------------------------------
// UV/Vis 1D
// -----------------------------------------------------------------------
function plotUv1D(event) {
    viewer = new jsv2.SpectraViewer1D("#" + GRAPH_ID, {
        name: "uv-viewer",
        width: getResponsiveWidth(),
        height: 400,
        margin: { top: 50, bottom: 50, right: 50, left: 90 },
        chartTitle: { text: "UV/Vis Spectrum", fontSize: 200, color: "black", underline: true },
        chartType: "1D-UV",
        simplifyTolerance: 0.005,
        dataset: [{
            dataArray: { x: event.detail.x, y: event.detail.y },
            isMirrorPlot: false,
            normalize: true,
            name: (event.detail.assignmentData && event.detail.assignmentData.name) || "UV Spectrum",
            style: { strokeColor: "red", strokeWidth: 1 },
        }],
        axis: {
            // Auto-fit to data range instead of fixed boundaries
            x: { enabled: true, label: { text: "Wavelength (nm)", fontSize: 15 }, ticks: 20 },
            y: { enabled: true, label: { text: "Absorbance",      fontSize: 15 }, ticks: 11 },
        },
        grid: {
            horizontal: { enabled: true, strokeColor: "steelblue", strokeWidth: 0.5, opacity: 0.1, ticks: 15 },
            vertical:   { enabled: true, strokeColor: "black",     strokeWidth: 1,   opacity: 1,   ticks: 10 },
        },
        zoomBox: { enabled: true, position: "top-right", width: 200, height: 100 },
        controls: {
            enabled: true,
            options: {
                zoom: { vertical: true, horizontal: true, max: 100 },
                pan:  { vertical: true, horizontal: true },
                reset: true,
                download: { formats: ["png", "svg", "pdf"], filename: "uv_spectrum" },
            },
        },
    });
    viewer.draw();
    window._jsvViewer = viewer;
    console.log("Viewer drawn for UV/Vis spectrum.");
}

// -----------------------------------------------------------------------
// LC-MS 1D
// -----------------------------------------------------------------------
function plotLcms1D(event) {
    viewer = new jsv2.SpectraViewer1D("#" + GRAPH_ID, {
        name: "lcms-viewer",
        width: getResponsiveWidth(),
        height: 400,
        margin: { top: 50, bottom: 50, right: 50, left: 90 },
        chartTitle: { text: "1D LC-MS Spectrum", fontSize: 20, color: "black" },
        chartType: "1D-LCMS",
        dataset: [{
            dataArray: { x: event.detail.x, y: event.detail.y },
            isMirrorPlot: false,
            simplifyTolerance: 0.001,  // For normalized NMR (max=1, baseline~0.000002)
            normalize: true,
            name: (event.detail.assignmentData && event.detail.assignmentData.name) || "LC-MS Spectrum",
            style: { strokeColor: "red", strokeWidth: 1 },
            id: "simulated",
        }],
        axis: {
            minBoundaries: { x: [0, 10], y: [-1, 1] },
            x: { enabled: true, label: { text: "m/z",       fontSize: 15 }, ticks: 20 },
            y: { enabled: true, label: { text: "Intensity", fontSize: 15 }, ticks: 11 },
        },
        grid: {
            horizontal: { enabled: true, strokeColor: "steelblue", strokeWidth: 0.5, opacity: 0.1, ticks: 15 },
            vertical:   { enabled: true, strokeColor: "black",     strokeWidth: 1,   opacity: 1,   ticks: 10 },
        },
        controls: {
            enabled: true,
            options: {
                zoom: { vertical: true, horizontal: true, max: 100 },
                pan:  { vertical: true, horizontal: true },
                reset: true,
                download: { formats: ["png", "svg", "pdf"], filename: "lcms_spectrum" },
            },
        },
    });
    viewer.draw();
    window._jsvViewer = viewer;
    console.log("Viewer drawn for LC-MS 1D spectrum.");
}

// -----------------------------------------------------------------------
// EI-MS 1D
// -----------------------------------------------------------------------
function plotEims1D(event) {
    viewer = new jsv2.SpectraViewer1D("#" + GRAPH_ID, {
        name: "eims-viewer",
        width: getResponsiveWidth(),
        height: 400,
        margin: { top: 50, bottom: 50, right: 50, left: 90 },
        chartTitle: { text: "1D EI-MS Spectrum", fontSize: 20, color: "black" },
        chartType: "1D-EIMS",
        dataset: [{
            dataArray: { x: event.detail.x, y: event.detail.y },
            isMirrorPlot: false,
            simplifyTolerance: 0.001,  // For normalized NMR (max=1, baseline~0.000002)
            normalize: true,
            name: (event.detail.assignmentData && event.detail.assignmentData.name) || "EI-MS Spectrum",
            style: { strokeColor: "red", strokeWidth: 1 },
            id: "simulated",
        }],
        axis: {
            minBoundaries: { x: [-1, 10], y: [-1, 1] },
            x: { enabled: true, label: { text: "m/z",       fontSize: 15 }, ticks: 20 },
            y: { enabled: true, label: { text: "Intensity", fontSize: 15 }, ticks: 11 },
        },
        grid: {
            horizontal: { enabled: true, strokeColor: "steelblue", strokeWidth: 0.5, opacity: 0.1, ticks: 15 },
            vertical:   { enabled: true, strokeColor: "black",     strokeWidth: 1,   opacity: 1,   ticks: 10 },
        },
        controls: {
            enabled: true,
            options: {
                zoom: { vertical: true, horizontal: true, max: 100 },
                pan:  { vertical: true, horizontal: true },
                reset: true,
                download: { formats: ["png", "svg", "pdf"], filename: "eims_spectrum" },
            },
        },
    });
    viewer.draw();
    window._jsvViewer = viewer;
    console.log("Viewer drawn for EI-MS 1D spectrum.");
}

// -----------------------------------------------------------------------
// NMR 2D
// -----------------------------------------------------------------------
function plotNmrml2D(event) {
    viewer = new jsv2.SpectraViewer2D("#" + GRAPH_ID, {
        debug: false,
        width: 650,
        height: 650,
        zoom_min: 0.1,
        simplify_tolerance: 0.01,
        dataset: {
            dataArray: { x: event.detail.x, y: event.detail.y, z: event.detail.z },
        },
        highlight: {
            restriction_spectrum_id: "Assignment",
            element_type: "peak",
            display: { fill: "rgba(238, 238, 255, 0.7)" },
            visible_only: false,
        },
        assignmentTable: {
            container_id: "#assignments",
            edit_mode: false,
            clusterNavigationID: "#cluster-navigation-1",
        },
    });
    viewer.draw();
    window._jsvViewer = viewer;
    console.log("Viewer drawn for NMRML 2D spectrum.");
}

// -----------------------------------------------------------------------
// NMR 1D
// -----------------------------------------------------------------------
function plotNmrml1D(event) {
    viewer = new jsv2.SpectraViewer1D("#" + GRAPH_ID, {
        name: "nmr-viewer",
        width: getResponsiveWidth(),
        height: 400,
        margin: { top: 50, bottom: 50, right: 50, left: 90 },
        chartTitle: { text: "1D NMR Spectrum", fontSize: 20, color: "black" },
        chartType: "1D-NMR",
        dataset: [{
            dataArray: { x: event.detail.x, y: event.detail.y },
            isMirrorPlot: false,
            simplifyTolerance: 0.001,  // For normalized NMR (max=1, baseline~0.000002)
            normalize: true,
            name: (event.detail.assignmentData && event.detail.assignmentData.name) || "NMR Spectrum",
            style: { strokeColor: "red", strokeWidth: 1 },
            id: "simulated",
        }],
        axis: {
            minBoundaries: { x: [-1, 12], y: [-0.1, 1.1] },
            x: { enabled: true, reverse: true, label: { text: "Chemical Shift (ppm)", fontSize: 15 }, ticks: 20 },
            y: { enabled: true,               label: { text: "Intensity",             fontSize: 15 }, ticks: 11 },
        },
        grid: {
            horizontal: { enabled: true, strokeColor: "steelblue", strokeWidth: 0.5, opacity: 0.1, ticks: 15 },
            vertical:   { enabled: true, strokeColor: "black",     strokeWidth: 1,   opacity: 1,   ticks: 10 },
        },
        controls: {
            enabled: true,
            options: {
                zoom: { vertical: true, horizontal: true, max: 100 },
                pan:  { vertical: true, horizontal: true },
                reset: true,
                download: { formats: ["png", "svg", "pdf"], filename: "nmr_spectrum" },
            },
        },
        assignmentTable: {
            enabled: true,
            containerID: "#assignments",
            clusterNavigationID: "#cluster-navigation-1",
            data: [event.detail.assignmentData],
            structure: {
                containerID: "#structure",
                use_jsmol: true,
                show_atom_numbers: true,
                show_hydrogen: true,
                width: 400,
                height: 400,
                format: "nmrml",
            },
        },
    });
    viewer.draw();
    window._jsvViewer = viewer;
    console.log("Viewer drawn for NMRML 1D spectrum.");
}